/**
 * AN-ACC Assessment API Service
 * Manages AN-ACC assessments using local storage
 */

import LocalStorageService from '../storage/LocalStorageService';
import { 
  createANACCAssessment, 
  calculateRUGADLTotal,
  classifyANACC
} from '../../data/models/ANACCAssessment';

class ANACCService {
  constructor() {
    this.storageKey = 'agedcare_anacc_assessments';
  }

  /**
   * Get assessment by resident ID
   */
  async getByResidentId(residentId) {
    try {
      const assessments = LocalStorageService.find(
        this.storageKey,
        a => a.residentId === residentId
      );
      
      // Return most recent
      if (assessments.length === 0) {
        return { success: false, error: 'Assessment not found' };
      }
      
      const sorted = assessments.sort((a, b) => 
        new Date(b.assessmentDate) - new Date(a.assessmentDate)
      );
      
      return { success: true, data: sorted[0] };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Create or update assessment
   */
  async save(assessmentData) {
    try {
      const assessment = createANACCAssessment(assessmentData);
      
      // Calculate RUG-ADL total
      if (assessment.rugAdl) {
        assessment.rugAdlTotal = calculateRUGADLTotal(assessment.rugAdl);
      }

      // Classify AN-ACC
      const classification = classifyANACC(assessment);
      assessment.anaccClass = classification.anaccClass;
      assessment.anaccClassDescription = classification.anaccClassDescription;
      assessment.classificationSource = classification.classificationSource;

      // Check if updating existing
      const existing = await this.getByResidentId(assessment.residentId);
      if (existing.success && existing.data.status === 'draft') {
        // Update existing draft
        const updated = LocalStorageService.updateItem(
          this.storageKey,
          existing.data.id,
          assessment
        );
        return { success: true, data: updated };
      } else {
        // Create new
        const created = LocalStorageService.addItem(this.storageKey, assessment);
        return { success: true, data: created };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate revenue forecast
   */
  async calculateRevenueForecast(residentId) {
    try {
      const assessmentResult = await this.getByResidentId(residentId);
      if (!assessmentResult.success) {
        return assessmentResult;
      }

      const assessment = assessmentResult.data;
      
      // Simplified revenue calculation (in production, use official NWC weightings)
      const baseDailyRate = 200; // Base rate
      const classMultiplier = assessment.anaccClass ? assessment.anaccClass * 15 : 0;
      const projectedDailySubsidy = baseDailyRate + classMultiplier;
      
      // Update assessment with forecast
      const updated = LocalStorageService.updateItem(this.storageKey, assessment.id, {
        projectedDailySubsidy,
        nwcWeighting: assessment.anaccClass,
        revenueForecast: {
          daily: projectedDailySubsidy,
          monthly: projectedDailySubsidy * 30,
          yearly: projectedDailySubsidy * 365
        }
      });

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new ANACCService();

